import { Location, type UUID } from '@cobliteam/common-types';
import { DatadogTrack } from '@cobliteam/datadog-rum';
import Alert from '@cobliteam/ui/components/Alert';
import type { EventSourcePolyfill } from 'event-source-polyfill';
import queryString from 'query-string';
import { EventSourceRequester } from '../../request/EventSourceRequester';
import type { Configuration } from '../../request/Types';
import { deviceStatusEventsTransformer } from './DeviceTransformer';
import type { DeviceStatusEvent, DeviceStatusEventAPIV2, LastLocationAPI } from './definitions';

const DEFAULT_RETRY_TIMEOUT_IN_MILLISECONDS = 3000;
const MAX_RETRY_TIMEOUT_IN_MILLISECONDS = 30000;
const MAX_RETRIES = 15;
const DEFAULT_DEVICES_SLIDE_SSE_TIME_SECONDS = 5;

export type FleetEventSourceConnectionStatus = 'connected' | 'reconnecting';

export class FleetEventSourceRequesterV2 {
  private static enableV2FleetEvents = false;
  private eventSourceRequester: EventSourceRequester;
  private devicesSlideSseTimeSeconds: number;

  private numberOfRetries = 0;
  private currentUrl?: string;
  private reconnectTimeout?: ReturnType<typeof setTimeout>;

  constructor(configuration: Configuration & { useEventSourceLibrary: boolean; devicesSlideSseTimeSeconds: number }) {
    this.eventSourceRequester = new EventSourceRequester(configuration);
    this.devicesSlideSseTimeSeconds = configuration.devicesSlideSseTimeSeconds || DEFAULT_DEVICES_SLIDE_SSE_TIME_SECONDS;
  }

  public static setEnableV2FleetEvents = (enableV2FleetEvents: boolean) => {
    this.enableV2FleetEvents = enableV2FleetEvents;
  };

  public setDevicesSlideSseTimeSeconds = (devicesSlideSseTimeSeconds: number) => {
    this.devicesSlideSseTimeSeconds = devicesSlideSseTimeSeconds;
  };

  private getRetryDelay = () => {
    const multiplier = 2 ** Math.min(this.numberOfRetries, 5);
    return Math.min(DEFAULT_RETRY_TIMEOUT_IN_MILLISECONDS * multiplier, MAX_RETRY_TIMEOUT_IN_MILLISECONDS);
  };

  private clearReconnectTimeout = () => {
    if (!this.reconnectTimeout) return;
    clearTimeout(this.reconnectTimeout);
    this.reconnectTimeout = undefined;
  };

  private trackConnectionFailure = ({
    phase,
    error,
    fleetId,
    readyState,
    eventType,
  }: {
    phase: 'onerror' | 'subscribe' | 'retry';
    error: unknown;
    fleetId: UUID;
    readyState?: number;
    eventType?: string;
  }) => {
    const errorMessage = error instanceof Error ? error.message : String(error);
    const ddError = new Error(`Fleet-events-v2 ${phase} failed - ${errorMessage}`);
    ddError.name = 'Fleet-events-v2';

    DatadogTrack.error(ddError, {
      EventSource: 'Fleet-events-v2',
      phase,
      fleetId,
      retryCount: this.numberOfRetries,
      url: this.currentUrl,
      readyState,
      eventType,
      originalMessage: errorMessage,
    });
  };

  public subscribeToDeviceStatus = (
    fleetId: UUID,
    callback: (data: DeviceStatusEvent[]) => void,
    vehicleGroupIds?: UUID[],
    onConnectionStatusChange?: (status: FleetEventSourceConnectionStatus) => void
  ): Promise<EventSource | EventSourcePolyfill> => {
    const queryParams: Record<string, string | number> = {
      refresh_interval_in_seconds: this.devicesSlideSseTimeSeconds,
    };

    const validVehicleGroupIds = vehicleGroupIds?.filter(Boolean) ?? [];

    if (validVehicleGroupIds.length > 0) {
      queryParams.wanted_vehicle_group_ids = validVehicleGroupIds.join(',');
    }

    const url = `/v2/fleet-events/${fleetId}?${queryString.stringify(queryParams)}`;
    this.currentUrl = url;
    this.clearReconnectTimeout();

    return this.eventSourceRequester
      .subscribe({ url })
      .then((eventSource) => {
        eventSource.onerror = (err: Event) => {
          onConnectionStatusChange?.('reconnecting');

          const target = err.target as EventSource | EventSourcePolyfill | null;
          const readyState = target?.readyState;

          // when 10 retries, send an error to Datadog
          if (this.numberOfRetries > 0 && this.numberOfRetries % 10 === 0) {
            this.trackConnectionFailure({
              phase: 'onerror',
              error: new Error(`Connection failed with event type ${err.type}`),
              fleetId,
              readyState,
              eventType: err.type,
            });
          }

          // Release from pool before closing to prevent memory leak
          if (this.currentUrl) {
            this.eventSourceRequester.unsubscribe(this.currentUrl);
          }

          eventSource.close();

          const currentLocationIsMonitoring = typeof window !== 'undefined' && window.location.href.includes(Location.MONITORING);

          if (currentLocationIsMonitoring && FleetEventSourceRequesterV2.enableV2FleetEvents) {
            if (this.numberOfRetries >= MAX_RETRIES) {
              this.trackConnectionFailure({
                phase: 'onerror',
                error: new Error(`Max retries (${MAX_RETRIES}) reached, stopping reconnection`),
                fleetId,
                readyState,
                eventType: err.type,
              });
              Alert.error('Não foi possível conectar ao rastreamento em tempo real. Recarregue a página para tentar novamente.');
              return;
            }

            const retryDelay = this.getRetryDelay();
            this.reconnectTimeout = setTimeout(() => {
              this.numberOfRetries += 1;
              this.subscribeToDeviceStatus(fleetId, callback, vehicleGroupIds, onConnectionStatusChange).catch((retryError) => {
                this.trackConnectionFailure({
                  phase: 'retry',
                  error: retryError,
                  fleetId,
                  readyState,
                  eventType: err.type,
                });
              });
            }, retryDelay);
          }
        };

        eventSource.onopen = () => {
          onConnectionStatusChange?.('connected');
          DatadogTrack.action('Fleet-events-v2 connection open successfully');
          this.clearReconnectTimeout();
          this.numberOfRetries = 0;
        };

        // @ts-expect-error
        eventSource.addEventListener('DeviceStatus[]', (evt: any) => {
          try {
            const translatedEventData: LastLocationAPI[] = this.translateEvent(evt);
            const deviceStatusData: DeviceStatusEvent[] = deviceStatusEventsTransformer(translatedEventData);

            if (callback) {
              callback(deviceStatusData);
            }
          } catch {
            Alert.error('Erro ao receber as informações de localização em tempo real');
          }
        });

        return eventSource;
      })
      .catch((error) => {
        onConnectionStatusChange?.('reconnecting');
        this.trackConnectionFailure({
          phase: 'subscribe',
          error,
          fleetId,
        });
        throw error;
      });
  };

  private translateEvent = (evt: any): LastLocationAPI[] => {
    const parsedEventData: DeviceStatusEventAPIV2[] = JSON.parse(evt.data);

    return parsedEventData.map((event) => {
      const eventTimeInMillis = new Date(event.event_time).getTime();

      const translatedEventData: LastLocationAPI = {
        ...event,
        speed: event.speed_in_kmh,
        time: eventTimeInMillis,
        time_in_millis: eventTimeInMillis,
        cobli_ingested_at: new Date(event.cobli_ingested_at).getTime(),
        processed_at: new Date(event.processed_at).getTime(),
      };

      return translatedEventData;
    });
  };

  public unsubscribe = () => {
    this.clearReconnectTimeout();
    this.numberOfRetries = 0;
    if (this.currentUrl) {
      this.eventSourceRequester.unsubscribe(this.currentUrl);
      this.currentUrl = undefined;
    }
  };
}

export default FleetEventSourceRequesterV2;
